import { Box, xcss } from '@atlaskit/primitives';
import TextArea from '@atlaskit/textarea';
import { token } from '@atlaskit/tokens';
import { submitFormOnEnter } from '@databases/form/Form';
import { defaultStoreUtils } from '@databases/redux';
import { selectFieldIdsByContentId } from '@databases/redux/cellSelections/selectors';
import { DatabaseFieldType, NumberFormats } from '@databases/types/database';
import { getNumberDisplayString } from '@databases/utils/database/field-types/number';
import React, {
  type FunctionComponent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useIntl } from 'react-intl-next';

import {
  MAX_POPOVER_EDIT_WIDTH,
  TEXT_VALUE_MAX_TEXT_AREA_HEIGHT,
  useAdjustTextAreaDimensions,
} from './popover-edit-common';
import { type EditComponentProps } from './types';

const textAreaContainerStyling = xcss({
  display: 'flex',
  flexDirection: 'row-reverse',
  boxSizing: 'border-box',
  position: 'relative',
  zIndex: 'dialog', // Ensure container sits above blue dots
});

const firstFieldStylings = xcss({
  flexDirection: 'row',
});

const textAreaMinHeightStyling = xcss({
  minHeight: 'size.400',
});

const NumberValuePopoverEdit: FunctionComponent<
  EditComponentProps<DatabaseFieldType.NUMBER>
> = ({
  onChange,
  onFocus,
  onBlur,
  autoFocus,
  value,
  isInvalid,
  field,
  isRenderedInSidePanel,
  isBulkEdit,
  contentId,
}) => {
  const { locale } = useIntl();
  const maxWidthReachedRef = useRef<boolean>(false);
  const textAreaRef = useRef<HTMLTextAreaElement>(null);

  const [inputValue, setInputValue] = useState(
    value !== '' ? getNumberDisplayString(value, field, locale) : ''
  );
  const store = defaultStoreUtils.useStore();

  const adjustTextAreaDimensions = useAdjustTextAreaDimensions({
    textAreaRef,
    maxWidthReachedRef,
    maxPopoverEditWidth: isRenderedInSidePanel
      ? textAreaRef.current?.scrollWidth
      : MAX_POPOVER_EDIT_WIDTH,
  });

  useEffect(() => {
    adjustTextAreaDimensions();
  }, [inputValue, adjustTextAreaDimensions]);

  const fieldIds = selectFieldIdsByContentId(store.getState(), {
    contentId,
  });

  const isFirstField = fieldIds.indexOf(field.id) === 0;

  function sanitizeNumberInputValue(inputValue: string): string {
    const isNegative = inputValue.startsWith('-');

    const sanitizedInput = inputValue.replace(/[^0-9.,]/g, '');

    return isNegative ? '-' + sanitizedInput : sanitizedInput;
  }

  const parseInput = useCallback(
    (localizedString: string) => {
      const formatter = new Intl.NumberFormat(locale);

      const parts = formatter.formatToParts(12345.6);
      let decimalSeparator = '.';
      let groupingSeparator = ',';
      for (const part of parts) {
        if (part.type === 'decimal') {
          decimalSeparator = part.value;
        } else if (part.type === 'group') {
          groupingSeparator = part.value;
        }
      }

      let withoutGroupingSeparators = localizedString.split(groupingSeparator).join('');

      // Take only the first occurrence of the decimal separator
      const decimalIndex = withoutGroupingSeparators.indexOf(decimalSeparator);
      if (decimalIndex !== -1) {
        withoutGroupingSeparators =
          withoutGroupingSeparators.slice(0, decimalIndex) +
          '.' +
          withoutGroupingSeparators
            .slice(decimalIndex + 1)
            .split(decimalSeparator)
            .join('');
      }

      return sanitizeNumberInputValue(withoutGroupingSeparators);
    },
    [locale]
  );

  const handleChange = useCallback(
    (e: React.FormEvent<HTMLTextAreaElement>): void => {
      let parsedValue = parseInput(e.currentTarget.value);
      setInputValue(e.currentTarget.value);

      if (field.settings.numberFormat.format === NumberFormats.PERCENT) {
        parsedValue = (Number(parsedValue) / 100).toString();
      }

      onChange(parsedValue, {
        type: DatabaseFieldType.NUMBER,
        value: parsedValue,
      });
    },
    [parseInput, field.settings.numberFormat.format, onChange]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => submitFormOnEnter(e),
    []
  );

  const handleFocus = useCallback(
    (e: React.FocusEvent<HTMLTextAreaElement>) => {
      // Put cursor to the end of the field
      e.currentTarget.setSelectionRange(
        e.currentTarget.value.length,
        e.currentTarget.value.length
      );

      adjustTextAreaDimensions();
      onFocus?.(e);
    },
    [adjustTextAreaDimensions, onFocus]
  );

  const handlePaste = useCallback(() => {
    // Using setTimeout to ensure the pasted content is available in the textarea
    // Setting to 10 milliseconds to maximize the amount of time while NOT being
    // perceivable to humans
    setTimeout(() => {
      adjustTextAreaDimensions();
    }, 10);
  }, [adjustTextAreaDimensions]);

  const handleBlur = useCallback(() => {
    onBlur?.();
    setInputValue(value !== '' ? getNumberDisplayString(value, field, locale) : '');
  }, [field, locale, onBlur, value]);

  return (
    <Box
      testId={`number-value-edit-expand-${isFirstField ? 'right' : 'left'}`}
      xcss={[
        textAreaContainerStyling,
        isFirstField && firstFieldStylings,
        isRenderedInSidePanel && textAreaMinHeightStyling,
      ]}
    >
      <TextArea
        id={'NumberTextArea'}
        style={{
          border: `${token('border.width.selected', '2px')} solid ${token(
            'color.background.accent.blue.subtle',
            '#579DFF'
          )}`,
          borderRadius: 'radius.small',
          textAlign: 'right',
          overflow: 'hidden',
          maxHeight: `${TEXT_VALUE_MAX_TEXT_AREA_HEIGHT}px`,
          position: isRenderedInSidePanel || isBulkEdit ? 'relative' : 'absolute',
          boxSizing: 'border-box',
        }}
        className="right-align-textarea"
        ref={textAreaRef}
        value={inputValue}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        isInvalid={isInvalid}
        autoFocus={autoFocus}
        onKeyDown={handleKeyDown}
        onPaste={handlePaste}
        rows={1}
        resize="none"
        wrap="off"
        data-testid="number-value-edit"
      />
    </Box>
  );
};

export default NumberValuePopoverEdit;
